/**
 * Disk Image detail page component.
 *
 * @module pages/DiskImageDetailPage
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Copy, Check } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  LoadingPage,
  Badge,
  getDiskImageStateBadgeVariant,
  ConfirmModal,
} from '@/components/ui';
import { useDiskImage, useDeleteDiskImage } from '@/hooks';
import { formatDistanceToNow, format } from 'date-fns';

/**
 * Disk image detail page
 */
export function DiskImageDetailPage() {
  const { id, groupId } = useParams<{ id: string; groupId?: string }>();
  const navigate = useNavigate();
  const { data: diskImage, isLoading, error } = useDiskImage(id!, groupId);
  const deleteMutation = useDeleteDiskImage();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const backPath = groupId
    ? `/sandbox-groups/${encodeURIComponent(groupId)}/disk-images`
    : '/disk-images';

  const handleDelete = () => {
    if (diskImage) {
      deleteMutation.mutate(diskImage.id, {
        onSuccess: () => navigate(backPath),
      });
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error || !diskImage) {
    return (
      <div>
        <PageHeader
          title="Disk Image Not Found"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Disk Images', href: backPath },
            { label: 'Not Found' },
          ]}
        />
        <Card>
          <CardBody>
            <p className="text-gray-500 dark:text-gray-400">
              The requested disk image could not be found.
            </p>
            <Link to={backPath}>
              <Button
                variant="secondary"
                className="mt-4"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Disk Images
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={diskImage.labels?.['name'] || diskImage.id}
        description={diskImage.id}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Disk Images', href: backPath },
          { label: diskImage.labels?.['name'] || diskImage.id },
        ]}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to={backPath}>
              <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader title="Basic Information" />
          <CardBody>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                  {diskImage.name || <span className="text-gray-500 dark:text-gray-400">-</span>}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex items-center gap-2 font-mono">
                  {diskImage.id}
                  <button
                    onClick={() => handleCopy(diskImage.id, 'id')}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Copy disk image ID"
                    title="Copy disk image ID"
                  >
                    {copiedField === 'id' ? (
                      <Check className="h-4 w-4 text-success-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Labels</dt>
                <dd className="mt-1">
                  {diskImage.labels && Object.keys(diskImage.labels).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(diskImage.labels as Record<string, string>).map(
                        ([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                          >
                            {key}: {value}
                          </span>
                        )
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                  )}
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        {/* Status */}
        <Card>
          <CardHeader title="Status" />
          <CardBody>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">State</dt>
                <dd className="mt-1">
                  <Badge
                    variant={getDiskImageStateBadgeVariant(diskImage.status?.state || 'Unknown')}
                  >
                    {diskImage.status?.state || 'Unknown'}
                  </Badge>
                </dd>
              </div>
              {diskImage.status?.errorMessage && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Error</dt>
                  <dd className="mt-1 text-sm text-danger-500">{diskImage.status.errorMessage}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                  {diskImage.status?.createdAt ? (
                    <>
                      {format(new Date(diskImage.status.createdAt), 'PPpp')}
                      <span className="text-gray-500 dark:text-gray-400 ml-2">
                        (
                        {formatDistanceToNow(new Date(diskImage.status.createdAt), {
                          addSuffix: true,
                        })}
                        )
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Last Updated
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                  {diskImage.status?.updatedAt ? (
                    <>
                      {format(new Date(diskImage.status.updatedAt), 'PPpp')}
                      <span className="text-gray-500 dark:text-gray-400 ml-2">
                        (
                        {formatDistanceToNow(new Date(diskImage.status.updatedAt), {
                          addSuffix: true,
                        })}
                        )
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        {/* Image Configuration */}
        <Card>
          <CardHeader title="Image Configuration" />
          <CardBody>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Base Image</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 font-mono break-all">
                  {diskImage.image?.base || '-'}
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>
      </div>

      {/* Dockerfile Content - Only shown for Dockerfile-based images */}
      {diskImage.dockerfileContent && (
        <Card className="mt-6">
          <CardHeader
            title="Dockerfile"
            action={
              <button
                onClick={() => handleCopy(diskImage.dockerfileContent!, 'dockerfile')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1 text-sm"
                aria-label="Copy Dockerfile content"
              >
                {copiedField === 'dockerfile' ? (
                  <>
                    <Check className="h-4 w-4 text-success-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            }
          />
          <CardBody>
            <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm font-mono text-gray-900 dark:text-gray-200 whitespace-pre-wrap">
              {diskImage.dockerfileContent}
            </pre>
          </CardBody>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Disk Image"
        message={`Are you sure you want to delete "${diskImage.labels?.['name'] || diskImage.id}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
